import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtUSD, num } from "@/lib/format";
import { toast } from "sonner";
import { Save, Crown } from "lucide-react";

const F = ({ label, id, value, onChange, placeholder }) => (
  <div>
    <Label htmlFor={id} className="text-sm">{label}</Label>
    <Input id={id} type="number" inputMode="decimal" data-testid={id} value={value}
      onChange={(e) => onChange(e.target.value)} className="mt-1.5 rounded-xl" placeholder={placeholder ?? "0"} />
  </div>
);

export default function ShiftCalculator() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.subscription?.plan === "pro" && user?.subscription?.status === "active";
  const [s, setS] = useState({
    rate: user?.profile?.hourly_rate ? String(user.profile.hourly_rate) : "",
    shiftLen: "12", regular: "12", overtime: "0", otMult: "1.5",
    night: "0", weekend: "0", other: "0", bonus: "0",
  });
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const r = useMemo(() => {
    const rate = num(s.rate), reg = num(s.regular), ot = num(s.overtime), mult = num(s.otMult) || 1.5;
    const base = rate * reg;
    const otPay = rate * mult * ot;
    const night = num(s.night) * reg;
    const weekend = num(s.weekend) * reg;
    const other = num(s.other) * reg;
    const bonus = num(s.bonus);
    const total = base + otPay + night + weekend + other + bonus;
    return { base, otPay, night, weekend, other, bonus, total };
  }, [s]);

  const save = async () => {
    if (!isPro) {
      toast.error("Upgrade to RN Level Up Pro to save unlimited calculations.");
      navigate("/upgrade");
      return;
    }
    try {
      await api.post("/shift-calculations", {
        name: `${s.shiftLen}hr shift @ ${fmtUSD(num(s.rate))}/hr`,
        inputs: s, results: r,
      });
      toast.success("Calculation saved!");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#18A6A6]">Shift Pay Calculator</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#0F2747]">What does this shift earn?</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Inputs */}
        <div className="space-y-4 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm lg:col-span-3">
          <F label="Hourly base rate ($)" id="rate" value={s.rate} onChange={(v) => set("rate", v)} placeholder="e.g. 45" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Shift length</Label>
              <Select value={s.shiftLen} onValueChange={(v) => { set("shiftLen", v); if (v !== "custom") set("regular", v); }}>
                <SelectTrigger data-testid="shift-length-select" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="8">8 hours</SelectItem>
                  <SelectItem value="10">10 hours</SelectItem>
                  <SelectItem value="12">12 hours</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <F label="Regular hours" id="regular" value={s.regular} onChange={(v) => set("regular", v)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <F label="Overtime hours" id="overtime" value={s.overtime} onChange={(v) => set("overtime", v)} />
            <F label="Overtime multiplier" id="otMult" value={s.otMult} onChange={(v) => set("otMult", v)} placeholder="1.5" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <F label="Night differential ($/hr)" id="night" value={s.night} onChange={(v) => set("night", v)} />
            <F label="Weekend differential ($/hr)" id="weekend" value={s.weekend} onChange={(v) => set("weekend", v)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <F label="Other differential ($/hr)" id="other" value={s.other} onChange={(v) => set("other", v)} />
            <F label="Bonus amount ($)" id="bonus" value={s.bonus} onChange={(v) => set("bonus", v)} />
          </div>
          <p className="text-xs text-slate-400">Enter 0 where something doesn't apply. Differentials apply across regular hours.</p>
        </div>

        {/* Results */}
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-2xl bg-gradient-to-br from-[#0F2747] to-[#16355F] p-6 text-white shadow-lg" data-testid="shift-result-card">
            <p className="text-xs font-bold uppercase tracking-widest text-[#18A6A6]">Estimated Gross Pay for This Shift</p>
            <p className="mt-2 font-heading text-5xl font-extrabold" data-testid="shift-total">{fmtUSD(r.total, 2)}</p>
            <div className="mt-5 space-y-2 border-t border-white/10 pt-4 text-sm">
              {[["Base pay", r.base], ["Overtime pay", r.otPay], ["Night differential", r.night],
                ["Weekend differential", r.weekend], ["Other differential", r.other], ["Bonus", r.bonus]].map(([k, v]) => (
                <div key={k} className="flex justify-between text-slate-200">
                  <span>{k}</span><span className="font-semibold text-white">{fmtUSD(v, 2)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-white/10 pt-2 font-heading text-base font-bold">
                <span>Total</span><span className="text-[#D6A84B]">{fmtUSD(r.total, 2)}</span>
              </div>
            </div>
          </div>
          <Button onClick={save} data-testid="save-shift-btn"
            className={`w-full rounded-full py-6 ${isPro ? "bg-[#18A6A6] hover:bg-[#148f8f]" : "bg-[#0F2747] hover:bg-[#16355F]"}`}>
            {isPro ? <><Save className="mr-2 h-4 w-4" /> Save Calculation</> : <><Crown className="mr-2 h-4 w-4 text-[#D6A84B]" /> Save Calculation (Pro)</>}
          </Button>
          <p className="text-center text-xs text-slate-400">Estimated gross income before taxes and deductions.</p>
        </div>
      </div>
    </div>
  );
}
